// Pure legend-derivation helpers shared by the print legend (and available to
// the on-map legend). No React, no styling.
//
// Key fact about the data model: aspect / slope / geomorphons are split into
// PER-CLASS artifacts (e.g. an "East (E)" layer, a "0 – 2°" layer), each
// independently toggleable. Their artifact names match the statistics
// breakdown TITLES. So the legend shows a class only when its artifact is
// actually visible — we filter the full breakdown down to the visible classes.
// Elevation is continuous (not per-class), so it is detected by name and shown
// as a gradient.
const getArtifactName = (artifact) => {
    var _a;
    try {
        return typeof ((_a = artifact === null || artifact === void 0 ? void 0 : artifact.metadata) === null || _a === void 0 ? void 0 : _a.getName) === 'function' ? artifact.metadata.getName() : '';
    }
    catch (_b) {
        return '';
    }
};
// Names (trimmed) of every currently-visible artifact. The per-class aspect /
// slope / geomorphon layers are matched against this set by title.
export const getVisibleArtifactNames = (loadedArtifacts, isArtifactVisible) => {
    const names = new Set();
    try {
        loadedArtifacts.forEach((artifact, id) => {
            if (!isArtifactVisible(id))
                return;
            const name = getArtifactName(artifact).trim();
            if (name)
                names.add(name);
        });
    }
    catch (_a) {
        /* noop */
    }
    return names;
};
// Inspect visible layers for the Elevation/Contours legend section: which
// contour intervals are shown (#4), whether to show the elevation gradient
// (suppressed when contours are monochrome, #3), and robust contour detection
// for ANY interval incl. custom (#2, via isContourName).
export const getContourElevationInfo = (loadedArtifacts, isArtifactVisible, artifactControls) => {
    // Keyed by value+unit (or the raw label when unparseable) so two layers at the
    // same interval collapse to one entry, as the old string-set dedupe did.
    const intervals = new Map();
    const monoColors = new Set();
    let showGradient = false;
    try {
        loadedArtifacts.forEach((artifact, id) => {
            var _a;
            if (!isArtifactVisible(id))
                return;
            const name = getArtifactName(artifact);
            const lower = name.toLowerCase();
            const mono = (_a = artifactControls === null || artifactControls === void 0 ? void 0 : artifactControls.get(id)) === null || _a === void 0 ? void 0 : _a.monochrome;
            if (isContourName(name)) {
                const parsed = parseContourInterval(name);
                const entry = parsed
                    ? { label: formatContourInterval(name), value: parsed.value, unit: parsed.unit }
                    : { label: formatContourInterval(name) };
                intervals.set(parsed ? `${parsed.value}|${parsed.unit}` : entry.label, entry);
                if (mono === null || mono === void 0 ? void 0 : mono.enabled)
                    monoColors.add(mono.color || '#000000');
                else
                    showGradient = true;
            }
            else if (lower.includes('elevation') ||
                lower.includes('colored_hillshade') ||
                lower.includes('colored hillshade')) {
                showGradient = true;
            }
        });
    }
    catch (_a) {
        /* noop */
    }
    const contourIntervals = [...intervals.values()];
    const monochromeColors = [...monoColors];
    return {
        contourIntervals,
        showGradient,
        monochromeColors,
        hasAny: showGradient || contourIntervals.length > 0 || monochromeColors.length > 0,
    };
};
// Elevation is continuous; detect a visible elevation/contour/hillshade layer
// by name (these are not split per class).
export const hasVisibleElevation = (loadedArtifacts, isArtifactVisible) => {
    let found = false;
    try {
        loadedArtifacts.forEach((artifact, id) => {
            if (found || !isArtifactVisible(id))
                return;
            const n = getArtifactName(artifact).toLowerCase();
            if (n.includes('elevation') ||
                n.includes('contour') ||
                n.includes('colored_hillshade') ||
                n.includes('colored hillshade') ||
                n === '0.5m' ||
                n === '1m' ||
                n === '2m' ||
                n === '3m') {
                found = true;
            }
        });
    }
    catch (_a) {
        /* noop */
    }
    return found;
};
import { slopeDegreesToPercentGrade, metersToFeet, feetToMeters, formatOneDecimal, } from './units';
import { isContourName, formatContourInterval, parseContourInterval, } from '../hooks/useLayerTypeDetection';
// A contour interval is a property of the generated layer, not a measurement of
// the terrain, so it always keeps its native value — no line was ever placed at
// a 6.6 ft step. When the reader has chosen a system other than the one the
// layer was built in, the translation follows in brackets: "2 m (6.6 ft)".
// Same system (or an unparseable name) prints the native label alone.
export const formatContourIntervalLabel = (entry, system) => {
    if (entry.value === undefined || !entry.unit)
        return entry.label;
    const nativeSystem = entry.unit === 'ft' ? 'imperial' : 'metric';
    if (nativeSystem === system)
        return entry.label;
    const converted = entry.unit === 'ft' ? feetToMeters(entry.value) : metersToFeet(entry.value);
    return `${entry.label} (${formatOneDecimal(converted)} ${entry.unit === 'ft' ? 'm' : 'ft'})`;
};
export const cleanBreakdown = (list) => Array.isArray(list) ? list.filter((i) => i && i.title && i.color) : [];
// Grade % (rise/run) for a slope class, parsed from its degree range in the
// title (e.g. "Gentle Slope (2 - 4°)" → "3–7%"). The open-ended top class
// (max ≥ 90°) reads ">N%". Returns null if no degree range is present.
export const slopeGradeLabel = (title) => {
    if (!title)
        return null;
    const m = title.match(/(\d+(?:\.\d+)?)\s*[-–—]\s*(\d+(?:\.\d+)?)\s*°/);
    if (!m)
        return null;
    const lo = parseFloat(m[1]);
    const hi = parseFloat(m[2]);
    if (!isFinite(lo) || !isFinite(hi))
        return null;
    const loPct = Math.round(slopeDegreesToPercentGrade(lo));
    if (hi >= 90)
        return `> ${loPct}%`;
    return `${loPct}–${Math.round(slopeDegreesToPercentGrade(hi))}%`;
};
// TWI / flow accumulation are continuous Blues gradients (not per-class). Detect
// a visible one by name. The flow check excludes twi/wetness so a hypothetical
// combined "Flow Accumulation / TWI" layer resolves to a single (TWI) bar rather
// than double-counting.
// Base continuous TWI (the Blues gradient). Excludes the percentile threshold
// layers, which are binary "wettest X%" overlays handled separately.
export const hasVisibleTwi = (loadedArtifacts, isArtifactVisible) => {
    let found = false;
    try {
        loadedArtifacts.forEach((artifact, id) => {
            if (found || !isArtifactVisible(id))
                return;
            const n = getArtifactName(artifact).toLowerCase();
            if ((n.includes('twi') || n.includes('wetness')) && !n.includes('percentile'))
                found = true;
        });
    }
    catch (_a) {
        /* noop */
    }
    return found;
};
export const getVisibleTwiPercentiles = (loadedArtifacts, isArtifactVisible) => {
    const out = [];
    const seen = new Set();
    try {
        loadedArtifacts.forEach((artifact, id) => {
            if (!isArtifactVisible(id))
                return;
            const n = getArtifactName(artifact).toLowerCase();
            if (!n.includes('percentile') || !(n.includes('twi') || n.includes('wetness')))
                return;
            // Handles "95th", "92_5th" and "92.5th".
            const m = n.match(/(\d+)(?:[._](\d+))?\s*(?:th|st|nd|rd)?[\s_]*percentile/);
            if (!m)
                return;
            const pct = parseFloat(m[2] ? `${m[1]}.${m[2]}` : m[1]);
            if (!isFinite(pct) || seen.has(pct))
                return;
            seen.add(pct);
            const topPct = Math.round((100 - pct) * 10) / 10;
            out.push({ pct, topPct, label: `Wettest ${topPct}%` });
        });
    }
    catch (_a) {
        /* noop */
    }
    return out.sort((a, b) => a.topPct - b.topPct); // wettest (tightest) first
};
export const hasVisibleFlow = (loadedArtifacts, isArtifactVisible) => {
    let found = false;
    try {
        loadedArtifacts.forEach((artifact, id) => {
            if (found || !isArtifactVisible(id))
                return;
            const n = getArtifactName(artifact).toLowerCase();
            if ((n.includes('flow') || n.includes('accumul')) && !n.includes('twi') && !n.includes('wetness')) {
                found = true;
            }
        });
    }
    catch (_a) {
        /* noop */
    }
    return found;
};
// Normalise a class label to its core for matching: drop parenthetical/degree/
// percent/number suffixes so a layer named "Gentle Slope" matches a breakdown
// title "Gentle Slope (2 - 4°)", and "East (E)" matches "East (E)". Equality of
// the core (not substring) avoids "Steep" matching "Very Steep".
const normalizeClassName = (s) => s
    .toLowerCase()
    .replace(/\([^)]*\)/g, ' ') // drop "(E)", "(2 - 4°)"
    .replace(/[0-9°%]/g, ' ') // drop numbers + symbols
    .replace(/[–—-]/g, ' ') // drop dashes
    .replace(/\s+/g, ' ')
    .trim();
// Slope per-class layers are named differently in the layer tree ("0 - 2°
// (0 - 3.49%): Flat") than in the stats breakdown ("Flat (0 - 2°)"), and even the
// label words diverge ("Gentle" vs "Gentle Slope", "Moderately" vs "Moderately
// Steep") — so neither exact nor label-normalised matching links them. Their one
// stable shared key is the lower degree bound (0, 2, 4, 8, … — unique per class),
// present in both strings as the first value of the "lo - hi°" / "> lo°" range.
// Returns it (as a string) or null when the name carries no degree range (aspect
// and geomorphon classes, which match fine on their labels).
const slopeLowerBoundKey = (s) => {
    const m = s.match(/>?\s*(\d+(?:\.\d+)?)\s*(?:[–—-]\s*\d+(?:\.\d+)?)?\s*°/);
    return m ? m[1] : null;
};
// Keep only the breakdown classes whose layer is currently visible. Matches by
// exact title first, then by lower degree bound (slope classes, see above), then
// by normalised class label (aspect/geomorphon classes) — exact-only matching
// misses the per-class layers whose stats title carries a degree suffix.
export const filterBreakdownToVisible = (breakdown, visibleNames) => {
    const normVisible = new Set();
    const slopeVisible = new Set();
    visibleNames.forEach((n) => {
        const k = normalizeClassName(n);
        if (k)
            normVisible.add(k);
        const sb = slopeLowerBoundKey(n);
        if (sb)
            slopeVisible.add(sb);
    });
    return cleanBreakdown(breakdown).filter((it) => {
        if (!it.title)
            return false;
        if (visibleNames.has(it.title.trim()))
            return true;
        const sb = slopeLowerBoundKey(it.title);
        if (sb && slopeVisible.has(sb))
            return true;
        const k = normalizeClassName(it.title);
        return k.length > 0 && normVisible.has(k);
    });
};
// Generic "all classes in one raster" layers (vs the per-class folders). Matched
// by EXACT name — a per-class layer like "Gentle Slope (2–4°)" contains the word
// "slope" but is NOT the combined raster, so substring matching would be wrong.
const COMBINED_NAMES = {
    aspect: ['aspect', 'aspect / slope composite'],
    slope: ['slope', 'aspect / slope composite'],
    geomorphons: ['geomorphons'],
};
const ALL_COMBINED_NAMES = new Set(Object.values(COMBINED_NAMES).flat());
// True for a combined "all classes in one raster" layer (Slope / Aspect /
// Geomorphons / composite). These drive the full-breakdown section for their own
// category and must NOT feed the per-class breakdown matching — otherwise a
// combined layer like "Slope" false-matches the same-named CLASS in another
// category's breakdown (geomorphons also has a "Slope" class, slope+geomorphons
// both have "Flat").
export const isCombinedLayerName = (name) => ALL_COMBINED_NAMES.has((name || '').trim().toLowerCase());
export const hasVisibleCombined = (loadedArtifacts, isArtifactVisible, category) => {
    const names = COMBINED_NAMES[category];
    let found = false;
    try {
        loadedArtifacts.forEach((artifact, id) => {
            if (found || !isArtifactVisible(id))
                return;
            if (names.includes(getArtifactName(artifact).trim().toLowerCase()))
                found = true;
        });
    }
    catch (_a) {
        /* noop */
    }
    return found;
};
export const deriveElevation = (breakdown) => {
    const items = cleanBreakdown(breakdown);
    if (!items.length)
        return null;
    const sorted = [...items].sort((a, b) => { var _a, _b; return parseFloat(((_a = a.key) === null || _a === void 0 ? void 0 : _a.split('_')[0]) || '0') - parseFloat(((_b = b.key) === null || _b === void 0 ? void 0 : _b.split('_')[0]) || '0'); });
    const stops = sorted.map((item, i) => {
        const pct = sorted.length === 1 ? 50 : (i / (sorted.length - 1)) * 100;
        return `${item.color} ${pct}%`;
    });
    const gradientCss = `linear-gradient(to top, ${stops.join(', ')})`;
    const elevations = [];
    items.forEach((item) => {
        const parts = (item.key || '').split('_');
        if (parts.length >= 2) {
            const lo = parseFloat(parts[0]);
            const hi = parseFloat(parts[1]);
            if (isFinite(lo))
                elevations.push(lo);
            if (isFinite(hi))
                elevations.push(hi);
        }
    });
    if (!elevations.length)
        return null;
    const min = Math.min(...elevations);
    const max = Math.max(...elevations);
    if (!isFinite(min) || !isFinite(max) || min === max)
        return null;
    return { gradientCss, min, mid: (min + max) / 2, max };
};
// Drawing type discriminator → legend glyph kind. Types not present here
// (label, sketch) are not shown in the legend. Circle measurements and stream
// highlights fold into the line-like kinds.
const FEATURE_KIND_BY_TYPE = {
    placemark: 'placemark',
    path: 'path',
    polygon: 'polygon',
    measurement: 'measurement',
    measurement_circle: 'measurement',
    contour_highlight: 'contour',
    stream_highlight: 'contour',
};
export const drawnTypeToFeatureKind = (type) => (type && FEATURE_KIND_BY_TYPE[type]) || null;
const FEATURE_FALLBACK_LABEL = {
    contour: 'Contour',
    polygon: 'Polygon',
    path: 'Path',
    placemark: 'Placemark',
    measurement: 'Measurement',
};
const FEATURE_PLURAL_LABEL = {
    contour: 'Contours',
    polygon: 'Polygons',
    path: 'Paths',
    placemark: 'Placemarks',
    measurement: 'Measurements',
};
const groupKey = (f) => `${f.kind}|${(f.color || '').toLowerCase()}|${(f.outlineColor || '').toLowerCase()}|${f.icon || ''}`;
const applyName = (defaultName, key, names) => {
    const override = key && names ? names[key] : undefined;
    return override && override.trim() ? override : defaultName;
};
// Builds the legend's rows WITHOUT applying the hide filter. Ungrouped → one row
// per feature (id = feature id). Grouped → one row per kind+colour+icon (id =
// "g:<key>"), so a whole group can be renamed/hidden as a unit. Names apply the
// rename overrides. The editor renders these rows (with checkboxes); the printed
// legend renders the hide-filtered subset (presentFeatures).
export const buildLegendRows = (features, opts = {}) => {
    if (!opts.group) {
        return features.map((f) => ({ ...f, name: applyName(f.name, f.id, opts.names) }));
    }
    const groups = new Map();
    for (const f of features) {
        const key = groupKey(f);
        const g = groups.get(key);
        if (g)
            g.count += 1;
        else
            groups.set(key, { item: f, count: 1 });
    }
    return Array.from(groups.entries()).map(([key, { item, count }]) => {
        const rowId = `g:${key}`;
        const def = count > 1 ? FEATURE_PLURAL_LABEL[item.kind] : item.name;
        return { ...item, id: rowId, name: applyName(def, rowId, opts.names) };
    });
};
// The rows the printed legend renders: built rows minus the hidden ones.
export const presentFeatures = (features, opts = {}) => {
    const rows = buildLegendRows(features, opts);
    return opts.hiddenIds && opts.hiddenIds.size
        ? rows.filter((r) => !r.id || !opts.hiddenIds.has(r.id))
        : rows;
};
const toFeature = (it, kind) => ({
    id: it.id,
    kind,
    name: (it.name && it.name.trim()) || FEATURE_FALLBACK_LABEL[kind],
    color: it.color || '#dc143c',
    icon: kind === 'placemark' ? it.icon : undefined,
    outlineColor: kind === 'polygon' ? it.outlineColor : undefined,
    fillOpacity: kind === 'polygon' ? it.fillOpacity : undefined,
    positions: it.positions,
});
// At most this many points per feature feed the frame test — enough to catch a
// large polygon/path straddling the frame edge without projecting every vertex.
const FRAME_SAMPLE_CAP = 16;
const sampleLngLats = (pts) => {
    if (pts.length <= FRAME_SAMPLE_CAP)
        return pts;
    const step = Math.ceil(pts.length / FRAME_SAMPLE_CAP);
    const out = [];
    for (let i = 0; i < pts.length; i += step)
        out.push(pts[i]);
    if (out[out.length - 1] !== pts[pts.length - 1])
        out.push(pts[pts.length - 1]);
    return out;
};
// Parses a saved artifact's geometryJson into representative lng/lat points for
// the frame test. Shapes mirror useServerArtifactRenderer's renderers. Returns
// [] when geometry is missing/unparseable (feature then never frame-filters out).
export const parseSavedFeaturePositions = (artifactType, geometryJson) => {
    var _a, _b;
    if (!geometryJson)
        return [];
    let g;
    try {
        g = JSON.parse(geometryJson);
    }
    catch (_c) {
        return [];
    }
    if (!g)
        return [];
    const arr = (list, lngKey, latKey) => Array.isArray(list)
        ? list
            .filter((p) => p && isFinite(p[lngKey]) && isFinite(p[latKey]))
            .map((p) => ({ lng: p[lngKey], lat: p[latKey] }))
        : [];
    switch (artifactType) {
        case 'placemark':
            return isFinite(g.lng) && isFinite(g.lat) ? [{ lng: g.lng, lat: g.lat }] : [];
        case 'measurement':
            return [
                ...(isFinite(g.startLng) && isFinite(g.startLat) ? [{ lng: g.startLng, lat: g.startLat }] : []),
                ...(isFinite(g.endLng) && isFinite(g.endLat) ? [{ lng: g.endLng, lat: g.endLat }] : []),
            ];
        case 'measurement_circle':
            return isFinite(g.centerLng) && isFinite(g.centerLat) ? [{ lng: g.centerLng, lat: g.centerLat }] : [];
        case 'path':
            return sampleLngLats(arr(g.points, 'lng', 'lat'));
        case 'polygon':
            return sampleLngLats(arr(g.vertices, 'lng', 'lat'));
        case 'contour_highlight':
            return sampleLngLats(arr(g.coordinates, 'lng', 'lat'));
        case 'stream_highlight':
            return sampleLngLats(arr(g.line_coordinates || ((_b = (_a = g.segments) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.lineCoordinates), 'lng', 'lat'));
        default:
            return [];
    }
};
// Pulls polygon styling (outline colour, fill opacity) out of a saved artifact's
// geometryJson — these live in the geometry blob, not on the artifact row.
export const parseSavedFeatureStyle = (artifactType, geometryJson) => {
    if (artifactType !== 'polygon' || !geometryJson)
        return {};
    try {
        const g = JSON.parse(geometryJson);
        return {
            outlineColor: typeof (g === null || g === void 0 ? void 0 : g.outlineColor) === 'string' ? g.outlineColor : undefined,
            fillOpacity: isFinite(g === null || g === void 0 ? void 0 : g.fillOpacity) ? g.fillOpacity : undefined,
        };
    }
    catch (_a) {
        return {};
    }
};
// Builds the legend feature list from saved server artifacts + unsaved tool
// items, keeping only the currently-visible drawn types. Saved come first so a
// report leads with its persisted content.
export const collectDrawnFeatures = (saved, unsaved) => {
    const out = [];
    for (const it of saved.items) {
        const kind = drawnTypeToFeatureKind(it.type);
        if (kind && saved.isVisible(it.id))
            out.push(toFeature(it, kind));
    }
    for (const it of unsaved.items) {
        const kind = drawnTypeToFeatureKind(it.type);
        if (kind && unsaved.isVisible(it.id, it.type || ''))
            out.push(toFeature(it, kind));
    }
    return out;
};
